import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import Login from "@/pages/Login";
import AcceptInvite from "@/pages/AcceptInvite";
import Dashboard from "@/pages/Dashboard";
import CreateAuction from "@/pages/CreateAuction";
import AuctionDetail from "@/pages/AuctionDetail";
import AdminUsers from "@/pages/AdminUsers";
import PeekAuditLog from "@/pages/PeekAuditLog";
import BopList from "@/pages/BopList";
import CreateBop from "@/pages/CreateBop";
import FreightList from "@/pages/FreightList";
import CreateFreight from "@/pages/CreateFreight";

const Root = () => {
  const { user, ready } = useAuth();
  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xs tracking-[0.3em] uppercase text-neutral-500">Loading...</div>
      </div>
    );
  }
  return user ? <Dashboard /> : <Navigate to="/login" replace />;
};

const AdminOnly = ({ children }) => {
  const { user, ready } = useAuth();
  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xs tracking-[0.3em] uppercase text-neutral-500">Loading...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;
  return children;
};

const SuperAdminOnly = ({ children }) => {
  const { user, ready } = useAuth();
  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xs tracking-[0.3em] uppercase text-neutral-500">Loading...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_super_admin) return <Navigate to="/" replace />;
  return children;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/accept-invite/:token" element={<AcceptInvite />} />
            <Route path="/" element={<Root />} />
            <Route
              path="/auctions/new"
              element={<AdminOnly><CreateAuction /></AdminOnly>}
            />
            <Route
              path="/auctions/:id"
              element={<ProtectedRoute><AuctionDetail /></ProtectedRoute>}
            />
            <Route
              path="/admin/users"
              element={<AdminOnly><AdminUsers /></AdminOnly>}
            />
            <Route
              path="/super-admin/peek-log"
              element={<SuperAdminOnly><PeekAuditLog /></SuperAdminOnly>}
            />
            <Route
              path="/bop-auctions"
              element={<ProtectedRoute><BopList /></ProtectedRoute>}
            />
            <Route
              path="/bop-auctions/new"
              element={<AdminOnly><CreateBop /></AdminOnly>}
            />
            <Route
              path="/freight"
              element={<ProtectedRoute><FreightList /></ProtectedRoute>}
            />
            <Route
              path="/freight/new"
              element={<AdminOnly><CreateFreight /></AdminOnly>}
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
